import React, { Fragment } from "react";
import { Controller, useForm } from "react-hook-form";
import { ApplicationSettings } from "../../../../core/api/models";
import ErrorMessage from "../../../../components/text/ErrorMessage";
import validator from "validator";
import { TimePeriodOnly } from "../../../../core/components/time-period/time-period-only";
import { ITimePeriodData, isTimePeriodValid } from "../../../../core/components/time-period/custom-time-period";

const UpdateForm: React.FunctionComponent<{
  onSubmit: (data: ApplicationSettings) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
  data?: ApplicationSettings;
  error?: string;
}> = (props) => {
  const { control, handleSubmit, errors } = useForm<ApplicationSettings>({
    reValidateMode: "onChange",
    mode: "onSubmit",
    shouldUnregister: true,
    defaultValues: props.data,
  });

  const renderEmail = () => {
    return (
      <div className="col-12">
        <div className="form-group font-weight-semibold mb-3">
          <label className="font-sm text-uppercase">Email<span className="text-danger">*</span></label>
          <Controller
            control={control}
            rules={{
              required: "Email is required",
            }}
            name="email"
            defaultValue={props.data?.email}
            render={({ onChange, value, name }) => (
              <input
                type="string"
                className="form-control rounded-sm font-weight-semibold"
                placeholder="Email"
                name={name}
                value={value}
                onChange={onChange}
                id="email"
              />
            )}
          />
          <ErrorMessage>{errors.email?.message}</ErrorMessage>
        </div>
      </div>
    );
  };

  const renderapplicationBaseUrl = () => {
    return (
      <div className="col-12">
        <div className="form-group font-weight-semibold mb-3">
          <label className="font-sm text-uppercase">Application Base Url<span className="text-danger">*</span></label>
          <Controller
            control={control}
            rules={{
              required: "Application Base Url is required",
              validate: {
                validUrl: (value) => validator.isURL(value) || "Invalid URL",
              },
            }}
            name="applicationBaseUrl"
            defaultValue={props.data?.applicationBaseUrl}
            render={({ onChange, value, name }) => (
              <input
                type="text"
                className="form-control rounded-sm font-weight-semibold"
                placeholder="Application Base Url"
                name={name}
                value={value}
                onChange={onChange}
                id="applicationBaseUrl"
              />
            )}
          />
          <ErrorMessage>{errors.applicationBaseUrl?.message}</ErrorMessage>
        </div>
      </div>
    );
  };

  const rendercrmBaseUrl = () => {
    return (
      <div className="col-12">
        <div className="form-group font-weight-semibold mb-3">
          <label className="font-sm text-uppercase">Crm Base Url<span className="text-danger">*</span></label>
          <Controller
            control={control}
            rules={{
              required: "Crm Base Url is required",
              validate: {
                validUrl: (value) => validator.isURL(value) || "Invalid URL",
              },
            }}
            name="crmBaseUrl"
            defaultValue={props.data?.crmBaseUrl}
            render={({ onChange, value, name }) => (
              <input
                type="text"
                className="form-control rounded-sm font-weight-semibold"
                placeholder="Crm Base Url"
                name={name}
                value={value}
                onChange={onChange}
                id="crmBaseUrl"
              />
            )}
          />
          <ErrorMessage>{errors.crmBaseUrl?.message}</ErrorMessage>
        </div>
      </div>
    );
  };
  const rendeTollFreeNumber = () => {
    return (
      <div className="col-12">
        <div className="form-group font-weight-semibold mb-3">
          <label className="font-sm text-uppercase">Toll Free Number<span className="text-danger">*</span></label>
          <Controller
            control={control}
            rules={{
              required: "TollFree Number is required",
              validate: {
                digits: (value) =>
                  !value ||
                  new RegExp("^\\d+$").test(value) ||
                  "Invalid Phone Number, should only contain digits",
              },
            }}
            name="tollFreeNumber"
            defaultValue={props.data?.tollFreeNumber}
            render={({ onChange, value, name }) => (
              <input
                type="text"
                className="form-control rounded-sm font-weight-semibold"
                placeholder="Toll Free Number"
                name={name}
                value={value}
                onChange={onChange}
                id="tollFreeNumber"
              />
            )}
          />
          <ErrorMessage>{errors.tollFreeNumber?.message}</ErrorMessage>
        </div>
      </div>
    );
  };

  const renderReceiptNoPrefix = () => {
    return (
      <div className="col-12">
        <div className="form-group font-weight-semibold mb-3">
          <label className="font-sm text-uppercase">Receipt No. Prefix<span className="text-danger">*</span></label>
          <Controller
            control={control}
            rules={{
              required: "Receipt No. Prefix is required",
              validate: {
                numberOnly: (value) =>
                  new RegExp("\\d+").test(value) || "Invalid number",
              },
            }}
            name="receiptNoPrefix"
            defaultValue={props.data?.receiptNoPrefix}
            render={({ onChange, value, name }) => (
              <input
                type="number"
                min={0}
                className="form-control rounded-sm font-weight-semibold"
                placeholder="Receipt No. Prefix"
                name={name}
                value={value}
                onChange={onChange}
                id="receiptNoPrefix"
              />
            )}
          />
          <ErrorMessage>{errors.receiptNoPrefix?.message}</ErrorMessage>
        </div>
      </div>
    );
  };
  const renderBrandColors = () => {
    return (
      <div className="col-12">
        <div className="form-group font-weight-semibold mb-3">
          <label className="font-sm text-uppercase">Brand Color</label>
          <Controller
            control={control}
            name="brandColor"
            defaultValue={props.data?.brandColor}
            render={({ onChange, value, name }) => (
              <input
                type="string"
                className="form-control rounded-sm font-weight-semibold"
                placeholder="Brand Color"
                name={name}
                value={value}
                onChange={onChange}
                id="brandColor"
              />
            )}
          />
        </div>
      </div>
    );
  };


  const renderAppointmentTimeSettings = () => {
    return (
      <div className="col-12">
        <div className="form-group font-weight-semibold mb-3">
          <label className="font-sm text-uppercase">Appointment Times</label>
          <Controller
            control={control}
            name="appointmentTime"
            defaultValue={props.data?.appointmentTime}
            render={({ onChange, value, name }) => (
              <Fragment>
                <TimePeriodOnly
                  onChange={(val: ITimePeriodData) => {
                    if (isTimePeriodValid(val)) {
                      onChange({ start: val.startTime, end: val.endTime });
                    } else {
                      onChange(undefined);
                    }
                  }}
                  defaultValue={{
                    endTime: props.data?.appointmentTime?.end,
                    startTime: props.data?.appointmentTime?.start,
                  }}
                  minDate={new Date()}
                  gap={5}
                ></TimePeriodOnly>
              </Fragment>
            )}
          />
          <ErrorMessage>
            {errors.isCustomerEmailMandatoryForEnquiry?.message}
          </ErrorMessage>
        </div>
      </div>
    );
  };

  const renderButton = () => {
    return (
      <div className="col-12">
        <div className="form-group font-weight-semibold mb-3 text-right pt-3">
          <button
            type="button"
            className="btn btn-outline-dark py-2 font-weight-semibold font-normal px-4 mr-1"
            onClick={props.onCancel}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary py-2 font-weight-semibold font-normal px-4 "
            disabled={props.isSubmitting}
          >
            Update
          </button>
        </div>
      </div>
    );
  };

  return (
    <form onSubmit={handleSubmit(props.onSubmit)}>
      <div className="p-4">
        <h4 className="mb-3 font-weight-semibold">
          Update Application Settings
        </h4>
        <div className="row gutter-10">
          {renderapplicationBaseUrl()}
          {rendercrmBaseUrl()}
          {renderEmail()}
          {rendeTollFreeNumber()}
          {/* {renderReceiptNoPrefix()} */}
          {/* {renderBrandColors()} */}
          {renderAppointmentTimeSettings()}
          <ErrorMessage>{props.error}</ErrorMessage>
          {renderButton()}
        </div>
      </div>
    </form>
  );
};

export default UpdateForm;
